/**
 * useOfficerCodeLookup - resolve a staff reference (UPN, email, or display
 * name as SharePoint stores the online-application FSR) to the person's
 * Core Director OPERATOR CODE.
 *
 * Bill 2026-09-11 RULING: "Make sure the initials are the user's associated
 * Core Operator initials and not just their first and last name derivations
 * when used throughout the app." When the roster cannot resolve someone the
 * answer is '' - render nothing - never a guess from their name.
 *
 * The roster (/v3/config/user-roles) is loaded once per page load and shared.
 */
import { useCallback, useEffect, useState } from 'react';
import type { UserRoleDto } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';

let rosterPromise: Promise<ReadonlyArray<UserRoleDto>> | null = null;

function norm(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** "Last, First" -> "first last" so either SharePoint spelling matches a displayName. */
function swapped(v: string): string {
  const i = v.indexOf(',');
  if (i < 0) return v;
  return `${v.slice(i + 1).trim()} ${v.slice(0, i).trim()}`.trim();
}

export function useOfficerCodeLookup(): (ref: string | null | undefined) => string {
  const client = useApiClient();
  const [roster, setRoster] = useState<ReadonlyArray<UserRoleDto>>([]);

  useEffect(() => {
    let live = true;
    rosterPromise ??= client.retail.config.users
      .list()
      .then((r) => {
        // Wire shape without ?email is { success, data: { users, roles, permissions, hierarchy } }
        // (V3ConfigFunction user-roles); the typed client says ReadonlyArray. Accept both.
        const d = (r as { success?: boolean; data?: unknown }).data;
        if (Array.isArray(d)) return d as ReadonlyArray<UserRoleDto>;
        const users = (d as { users?: unknown } | undefined)?.users;
        return Array.isArray(users) ? (users as ReadonlyArray<UserRoleDto>) : [];
      })
      .catch(() => []);
    void rosterPromise.then((rows) => { if (live) setRoster(rows); });
    return () => { live = false; };
  }, [client]);

  return useCallback((ref) => {
    const v = norm(ref);
    if (!v || roster.length === 0) return '';
    const local = v.includes('@') ? v.split('@')[0] ?? '' : '';
    const hit = roster.find((u) => {
      const email = norm(u.email);
      const name = norm(u.displayName);
      return (
        (email && (email === v || (local && email.split('@')[0] === local))) ||
        (name && (name === v || name === swapped(v) || swapped(name) === v))
      );
    });
    return (hit?.officerCode ?? '').trim().toUpperCase();
  }, [roster]);
}

/**
 * useOfficerNameLookup - the owning officer's display name for a cart, from the same roster:
 * by Core operator code first, then by the cart's assigned-user e-mail. '' when the roster
 * cannot resolve them (render the code instead - never a guess).
 * Bill 2026-10-13: "Show all" carts must show whose cart it is.
 */
export function useOfficerNameLookup(): (officerCode: string | null | undefined, email?: string | null) => string {
  const client = useApiClient();
  const [roster, setRoster] = useState<ReadonlyArray<UserRoleDto>>([]);

  useEffect(() => {
    let live = true;
    rosterPromise ??= client.retail.config.users
      .list()
      .then((r) => {
        const d = (r as { success?: boolean; data?: unknown }).data;
        if (Array.isArray(d)) return d as ReadonlyArray<UserRoleDto>;
        const users = (d as { users?: unknown } | undefined)?.users;
        return Array.isArray(users) ? (users as ReadonlyArray<UserRoleDto>) : [];
      })
      .catch(() => []);
    void rosterPromise.then((rows) => { if (live) setRoster(rows); });
    return () => { live = false; };
  }, [client]);

  return useCallback((officerCode, email) => {
    if (roster.length === 0) return '';
    const code = (officerCode ?? '').trim().toUpperCase();
    const mail = norm(email);
    const hit = (code && roster.find((u) => (u.officerCode ?? '').trim().toUpperCase() === code))
      || (mail && roster.find((u) => norm(u.email) === mail))
      || undefined;
    return (hit?.displayName ?? '').trim();
  }, [roster]);
}

/** The officer's e-mail (UPN) for a Core operator code, from the same roster; '' when unknown. */
export function useOfficerEmailLookup(): (officerCode: string | null | undefined) => string {
  const client = useApiClient();
  const [roster, setRoster] = useState<ReadonlyArray<UserRoleDto>>([]);
  useEffect(() => {
    let live = true;
    rosterPromise ??= client.retail.config.users
      .list()
      .then((r) => {
        const d = (r as { success?: boolean; data?: unknown }).data;
        if (Array.isArray(d)) return d as ReadonlyArray<UserRoleDto>;
        const users = (d as { users?: unknown } | undefined)?.users;
        return Array.isArray(users) ? (users as ReadonlyArray<UserRoleDto>) : [];
      })
      .catch(() => []);
    void rosterPromise.then((rows) => { if (live) setRoster(rows); });
    return () => { live = false; };
  }, [client]);
  return useCallback((officerCode) => {
    const code = (officerCode ?? '').trim().toUpperCase();
    if (!code || roster.length === 0) return '';
    return (roster.find((u) => (u.officerCode ?? '').trim().toUpperCase() === code)?.email ?? '').trim();
  }, [roster]);
}
